import React from 'react';
import { useToasts, FileUpload, Spinner, Flex } from '@applyboard/crystal-ui';
import { useEventTracker, useTranslation } from '@applyboard/ui-utils';
import { useExtractPassportInfo } from '../data';

interface PassportForm {
  givenName: string;
  familyName: string;
  birthDate: string;
  citizenshipCountryCode: string;
  gender: string;
  passportNumber: string;
  passport: File;
  passportExpiry: string;
}

export function PassportUpload({
  onUploadPassportSuccessful,
}: {
  onUploadPassportSuccessful: (ocrValues: PassportForm) => void;
}) {
  const { extractPassportInfo, isExtractingPassportInfo } = useExtractPassportInfo();
  const toasts = useToasts();
  const { trackEvent } = useEventTracker();
  const { t } = useTranslation();

  const uploadFileCallback = (
    file: File,
    params: {
      onSuccess: (fileId: string) => void;
      onError: (errorMessage?: string) => void;
    },
  ) => {
    extractPassportInfo(
      { file },
      {
        onSuccess: response => {
          if (response && Object.keys(response).length) {
            onUploadPassportSuccessful({
              givenName: response.name,
              familyName: response.surname,
              birthDate: new Date(response.birth_date).toISOString(),
              citizenshipCountryCode:
                response.nationality && response.nationality.substr(0, 2).toUpperCase(),
              gender: response.sex && response.sex.toLowerCase() == 'm' ? 'male' : 'female',
              passportNumber: response.document_number,
              passport: file,
              passportExpiry: new Date(response.expiry_date).toISOString(),
            });

            toasts.positive(t('passportAutoFill.fileScanSuccess'));

            trackEvent({
              data: {
                givenName: !!response.name,
                familyName: !!response.surname,
                birthDate: !!response.birth_date,
                gender: !!response.sex,
                citizenshipCountryCode: !!response.nationality,
                passportNumber: !!response.document_number,
                passportExpiry: !!response.expiry_date,
              },
              event: 'Passport OCR - Upload Successful',
              group: 'Student Intake Form',
            });
          } else {
            toasts.warning(t('passportAutoFill.noPassportInfoFoundWarning'));
          }

          params.onSuccess(response.document_number || '');
        },
        onError: () => {
          params.onError(t('passportAutoFill.uploadFileError'));

          trackEvent({
            event: 'Passport OCR - Upload Unsuccessful',
            group: 'Student Intake Form',
          });
        },
      },
    );
  };

  if (isExtractingPassportInfo)
    return (
      <Flex align="center" justify="center">
        <Spinner size="xl" />
      </Flex>
    );

  return (
    <FileUpload
      allowedFileTypes={['.jpg', '.jpeg', '.png', '.pdf']}
      helpText={t('passportAutoFill.fileUploadHelpText')}
      label={t('passportAutoFill.autofillWithPassport')}
      maxFileSize={10000000} // 10 MB
      size="sm"
      value={[]}
      onChange={() => {}}
      uploadFile={uploadFileCallback}
    >
      <FileUpload.Button>{t('passportAutoFill.fileUploadButton')}</FileUpload.Button>
    </FileUpload>
  );
}
